import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ModalParagraph} from '@/components/ui/modal-atoms/ModalAtoms'
import {useTranslation} from '@/translations/i18n'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {DialogStore} from '@ariakit/react'
import {Text} from '@/components/ui/text/Text'
import {QUERY_KEYS} from '@/queryClient'
import {useCreateTreatmentRequest} from '../../services/useCreateTreatmentRequest'
import {useQueryClient} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {AxiosError} from 'axios'
import {FormErrorResponse} from '@/types'
import {remapTreatmentRequestToPayload} from '../../utils'
import {HttpCreateTreatmentRequestPayload} from '../../services/treatment-requests.http'
import {CreateTreatmentRequestValidationSchema} from '../../types'
import {useForm, useWatch, FormProvider} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {toast} from '@/utilities/toast/toast'
import {useConversationForm} from '@/features/conversations/hooks/useConversationForm'
import {routes} from '@/utilities/constants/routes'

export const CreateTreatmentRequestConfirmationModal = ({
    dialogStore,
    onErrorConfirmationModal,
    defaultValues
}: {
    dialogStore: DialogStore
    onErrorConfirmationModal: (errors: AxiosError<{errors: FormErrorResponse[]}>) => void
    defaultValues: CreateTreatmentRequestValidationSchema
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const form = useForm<CreateTreatmentRequestValidationSchema>({
        resolver: zodResolver(CreateTreatmentRequestValidationSchema),
        defaultValues
    })
    const patient = useWatch({control: form.control, name: 'patient'})

    const {conversationForm, createConversation} = useConversationForm({
        errorRedirectPath: routes.TREATMENT_REQUESTS_IN_REVIEW.path
    })

    // mutations
    const {mutate: createTreatmentRequestMutation, isPending: isPendingCreateTreatmentRequest} =
        useCreateTreatmentRequest({
            onSuccess: async treatmentRequest => {
                await createConversation(treatmentRequest.patientId)
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                toast.success(
                    <Flexbox direction="column">
                        <Text size="textSm" weight="500">
                            {t('treatment_requests:create_request_success')}
                        </Text>
                        <Text size="textSm" palette="neutral" shade="500">
                            {patient.fullName}
                        </Text>
                    </Flexbox>
                )

                navigate(`/treatment-requests/${treatmentRequest.id}`)
            },
            onError: onErrorConfirmationModal
        })

    const handleConfirmSubmit = form.handleSubmit(formValues =>
        createTreatmentRequestMutation(
            HttpCreateTreatmentRequestPayload.parse(remapTreatmentRequestToPayload(formValues, false))
        )
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPendingCreateTreatmentRequest}
                conversationForm={conversationForm}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('treatment_requests:add_treatment_requests_title_extended')}</DialogHeading>
                        <ModalParagraph>{t('treatment_requests:new_assignment')}</ModalParagraph>
                        <Text size="textSm">{patient.fullName}</Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
